import React from 'react';
import { Hash, Eye, Trash2, Calendar, Clock, Tag, AlertTriangle, MapPin } from 'lucide-react';
import type { Database } from '../../lib/database.types';

type WorkOrder = Database['public']['Tables']['work_orders']['Row'];
type Customer = Database['public']['Tables']['customers']['Row'];

interface WorkOrderCardProps {
  order: WorkOrder;
  customer?: Customer;
  onDelete: (id: string) => Promise<void>;
  onDeleteClick: (id: string) => Promise<void>;
  onView: () => void;
  isDeleting: boolean;
}

export const WorkOrderCard: React.FC<WorkOrderCardProps> = ({ 
  order, 
  customer,
  onDelete,
  onDeleteClick,
  onView,
  isDeleting
}) => {
  const statusColors = {
    'Received': 'bg-gray-100 text-gray-800',
    'Customer Notified': 'bg-blue-100 text-blue-800',
    'Awaiting Customer Response': 'bg-yellow-100 text-yellow-800',
    'Waiting for Parts': 'bg-orange-100 text-orange-800',
    'Technician Ordering Parts': 'bg-purple-100 text-purple-800',
    'Unable to Reach Customer': 'bg-red-100 text-red-800',
    'Canceled': 'bg-red-100 text-red-800',
    'Reassigned': 'bg-indigo-100 text-indigo-800',
    'Visit Scheduled': 'bg-blue-100 text-blue-800',
    'Visit Completed': 'bg-green-100 text-green-800',
    'Needs Second Opinion': 'bg-yellow-100 text-yellow-800',
    'Under Research': 'bg-purple-100 text-purple-800',
    'Billed': 'bg-green-100 text-green-800',
    'Paid': 'bg-emerald-100 text-emerald-800'
  };

  const priorityColors = {
    low: 'bg-gray-100 text-gray-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    emergency: 'bg-red-100 text-red-800',
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div 
        onClick={onView}
        className="flex items-start justify-between cursor-pointer group hover:bg-gray-50 -m-6 p-6 rounded-lg transition-colors"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h3 className="text-lg font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                {customer?.first_name} {customer?.last_name}
              </h3>
              <div className="flex items-center text-gray-500">
                <Hash className="w-4 h-4 mr-1" />
                <span className="text-sm font-mono">{order.work_order_number || 'N/A'}</span>
              </div>
              <span className={`px-2.5 py-0.5 rounded-full text-sm font-medium ${statusColors[order.status]}`}>
                {order.status}
              </span>
              <span className={`px-2.5 py-0.5 rounded-full text-sm font-medium ${priorityColors[order.priority]}`}>
                {order.priority}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onView();
                }}
                className="p-2 text-gray-400 hover:text-blue-500 rounded-full hover:bg-white/50"
                title="View details"
              >
                <Eye className="w-5 h-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  void onDeleteClick(order.id);
                }}
                disabled={isDeleting}
                className="p-2 text-gray-400 hover:text-red-500 rounded-full hover:bg-white/50 disabled:opacity-50"
                title="Delete work order"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {order.scheduled_date && (
              <div className="flex items-center text-gray-600">
                <Calendar className="w-5 h-5 mr-2" />
                <span>{new Date(order.scheduled_date).toLocaleDateString()}</span>
              </div>
            )}
            {order.estimated_duration && (
              <div className="flex items-center text-gray-600">
                <Clock className="w-5 h-5 mr-2" />
                <span>{order.estimated_duration} minutes</span>
              </div>
            )}
            <div className="flex items-center text-gray-600">
              <Tag className="w-5 h-5 mr-2" />
              <span>{order.service_type}</span>
            </div>
            {(order.svo || order.model_number || order.serial_number) && (
              <div className="col-span-2 grid grid-cols-3 gap-4 bg-gray-50 p-3 rounded-lg mt-2">
                {order.svo && (
                  <div>
                    <span className="text-xs text-gray-500">SVO</span>
                    <p className="text-sm font-medium">{order.svo}</p>
                  </div>
                )}
                {order.model_number && (
                  <div>
                    <span className="text-xs text-gray-500">Model Number</span>
                    <p className="text-sm font-medium">{order.model_number}</p>
                  </div>
                )}
                {order.serial_number && (
                  <div>
                    <span className="text-xs text-gray-500">Serial Number</span>
                    <p className="text-sm font-medium">{order.serial_number}</p>
                  </div>
                )}
              </div>
            )}
            {order.brand && (
              <div className="flex items-center text-gray-600">
                <AlertTriangle className="w-5 h-5 mr-2" />
                <span>{order.brand}</span>
              </div>
            )}
          </div>

          <p className="text-gray-700">{order.description}</p>

          {customer && (
            <div className="flex items-center text-gray-600">
              <MapPin className="w-5 h-5 mr-2" />
              <span>
                {customer.address}, {customer.city}, {customer.state} {customer.zip_code}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};